import Immutable from 'immutable';
import { TimelineData } from '../records';

// Recurses through the timeline data and builds up a hash mapping the id of the
// bar records to all the paths that lead to copies of that bar (there can be
// multiple).
//
// The path will be the index of each parent followed by the record id.
//
// For example, assume we have a timeline that shows records A, B, A, B. Let's
// call them A1, B1, A2, B2. We could have paths of 0-A1, 0-B1, 1-A2, 2-B2. If
// there are multiple header columns then we coule have something like
// 0-2-1-A1.
//
// We could instead use the header record ids, but unfortunately they were not
// passed from the server, nor is it easy for the server to provide that
// information. It would be an easy switch in the future if we wantd to
// change the id scheme.
function collectRecordIds(
  timelineRows,
  inheritedPathParents = [],
  recordIds = {}
) {
  timelineRows.forEach((row, rowIndex) => {
    let pathParents = [...inheritedPathParents, rowIndex.toString()];
    if (row['children']) {
      collectRecordIds(row['children'], pathParents, recordIds);
    } else {
      row['rows'].forEach(row => {
        const rowRecordId = row['recordId'];
        const rowId = `${pathParents.join('-')}-${rowRecordId}`;

        if (!recordIds[rowRecordId]) {
          recordIds[rowRecordId] = [];
        }

        recordIds[rowRecordId].push(rowId);
      });
    }
  });

  return recordIds;
}

// Records can show up more than once on the timeline. As such, we need to take
// the list of dependency links from the server and create duplicate links that
// point to the duplicate records.
//
// For example, assume we have a timeline that shows records A, B, A, B. Let's
// call them A1, B1, A2, B2. If there is a dependency link between A and B, then
// we create four dependency links A1 -> B1, A1 -> B2, A2 -> B1, A2 -> B2.
//
// When we are rendering these links, we have to connect the link to the proper
// bar, knowing there might be duplicates of the bar for a record. So the new
// dependency links we create will have an id that uniquely identifies the bar
// based off the path used to get to it.
function fanOutDependencyLinks(
  timelineRows,
  dependencyLinks,
  fannedOutDependencyLinks = []
) {
  // Loop over all the rows and collect the record id and the path to that record
  const recordIdsToPaths = collectRecordIds(timelineRows);

  // Loop over each of the dependencies and create a new dependency that points
  // to the start and end rows for each permutation of the two.
  dependencyLinks.forEach(link => {
    const childRecordId = link['child_record_id'];
    const parentRecordId = link['parent_record_id'];

    recordIdsToPaths[parentRecordId].forEach(parentPath => {
      recordIdsToPaths[childRecordId].forEach(childPath => {
        fannedOutDependencyLinks.push({
          ...link,
          parentPath,
          childPath,
        });
      });
    });
  });

  return fannedOutDependencyLinks;
}

export default (state = Immutable.Map(), action) => {
  switch (action.type) {
    case 'HYDRATE_TIMELINE_CONFIG': {
      const { data, timelineType, dependencyLinks } = action.payload;

      return new TimelineData({
        data,
        timelineType,
        dependencyLinks: dependencyLinks
          ? fanOutDependencyLinks(data, dependencyLinks)
          : null,
      });
    }
    default:
      return state;
  }
};
